import fs from 'node:fs'
import path from 'node:path'
import { stripVTControlCharacters } from 'node:util'

import { parseDotenvFile, pullVercelEnvValue } from './env-pull.js'
import { runVercel } from './runner.js'
import type { VercelDeployResult, VercelRunner } from './types.js'

export {
  guardProjectForDeploy,
  type LockfileGuardStatus,
  type LockfileRefresher,
  type ProjectDeployGuard
} from '@next-adapter/init/deploy/guard.js'

/** Production builds run remotely; give the deploy a long window. */
const DEPLOY_TIMEOUT_MS = 900_000
const ENV_ADD_TIMEOUT_MS = 30_000
/** Failed deploys can carry full build logs; only the tail is useful inline. */
const DETAIL_MAX_LINES = 15

/**
 * Keys from `.env.local` that must never be pushed to the Vercel project:
 * - `VERCEL_OIDC_TOKEN` is Vercel-owned link state, not app config.
 * - `BETTERSTART_AUTH_URL` / `NEXT_PUBLIC_BETTERSTART_AUTH_URL` hold the local
 *   dev origin (`http://localhost:<port>`); in production better-auth derives
 *   the base URL from the request and the client falls back to
 *   `window.location.origin`, so pushing the localhost value would break auth.
 */
const ENV_SYNC_SKIP_KEYS = new Set([
  'VERCEL_OIDC_TOKEN',
  'BETTERSTART_AUTH_URL',
  'NEXT_PUBLIC_BETTERSTART_AUTH_URL'
])

export interface DeployEnvVar {
  key: string
  value: string
}

export interface EnvSyncResult {
  synced: string[]
  failed: string[]
}

export interface VercelDeployOptions {
  env?: NodeJS.ProcessEnv
  /** Receives each non-empty Vercel stdout/stderr line while the deploy runs. */
  onLogLine?: (line: string) => void
}

/** True for values that are scaffold placeholders rather than real config. */
function isPlaceholderValue(value: string): boolean {
  return value === 'postgresql://...' || value.startsWith('your_')
}

/**
 * Collect the env vars worth pushing to the Vercel project's production
 * environment: every non-empty, non-placeholder `.env.local` value except the
 * skip-listed keys and anything already present in production. Existing
 * production values are never overwritten — a connected resource or local env
 * sync can provide `DATABASE_URL`, and a connected Blob store manages
 * `BLOB_READ_WRITE_TOKEN` on the project itself.
 */
export function collectDeployEnvVars(
  cwd: string,
  existingProductionKeys: Set<string>
): DeployEnvVar[] {
  const local = parseDotenvFile(path.join(cwd, '.env.local'))
  const vars: DeployEnvVar[] = []

  for (const [key, value] of local) {
    if (ENV_SYNC_SKIP_KEYS.has(key)) continue
    if (!value || isPlaceholderValue(value)) continue
    if (existingProductionKeys.has(key)) continue
    vars.push({ key, value })
  }

  return vars
}

/**
 * Push the missing production env vars to the linked Vercel project before
 * deploying. Reads the project's current production environment through a temp
 * `vercel env pull` (never touching `.env.local`), then adds each missing key
 * with `vercel env add <key> production`, feeding the value over stdin. A pull
 * failure degrades to "assume nothing exists" — per-key `env add` failures
 * (e.g. a key that does exist after all) are collected, never thrown.
 */
export async function syncVercelProductionEnv(
  runner: VercelRunner,
  cwd: string,
  env?: NodeJS.ProcessEnv
): Promise<EnvSyncResult> {
  const existingKeys =
    (await pullVercelEnvValue(
      runner,
      cwd,
      (filePath) => new Set(parseDotenvFile(filePath).keys()),
      env,
      'production'
    )) ?? new Set<string>()

  const synced: string[] = []
  const failed: string[] = []

  for (const { key, value } of collectDeployEnvVars(cwd, existingKeys)) {
    const add = await runVercel(runner, ['env', 'add', key, 'production'], {
      cwd,
      mode: 'capture',
      timeoutMs: ENV_ADD_TIMEOUT_MS,
      env,
      stdinInput: `${value}\n`
    })

    if (add.success) synced.push(key)
    else failed.push(key)
  }

  return { synced, failed }
}

/**
 * Make sure the project deploys with the Next.js framework preset. The flow
 * may create the Vercel project with `vercel projects add` during the deploy
 * step (required for the name-conflict probing), which leaves the project's
 * Framework Preset as "Other" — the build then ignores the Next.js output and
 * every route 404s.
 * A `vercel.json` with `framework: "nextjs"` fixes the preset per deployment,
 * so write one when the project has none. An existing `vercel.json` is
 * user-owned and never modified. The file is intentionally permanent (not
 * guard-restored): it is correct project config for any future deploy.
 */
export function ensureVercelJsonFramework(cwd: string): 'created' | 'exists' {
  const vercelJsonPath = path.join(cwd, 'vercel.json')
  if (fs.existsSync(vercelJsonPath)) {
    return 'exists'
  }

  fs.writeFileSync(
    vercelJsonPath,
    `${JSON.stringify(
      { $schema: 'https://openapi.vercel.sh/vercel.json', framework: 'nextjs' },
      null,
      2
    )}\n`,
    'utf-8'
  )
  return 'created'
}

/**
 * Extract the deployed URL from `vercel deploy --prod` output. Newer CLIs print
 * a `Production: https://…` line (the canonical domain) to stderr; stdout is
 * the raw deployment URL. Prefer the production alias, fall back to stdout.
 */
export function parseDeployedUrl(stdout: string, stderr: string): string | undefined {
  const plainErr = stripVTControlCharacters(stderr)
  const production = plainErr.match(/Production:\s+(https:\/\/\S+)/i)?.[1]
  if (production) return production

  const fromStdout = stripVTControlCharacters(stdout).match(/https:\/\/\S+/)?.[0]
  return fromStdout
}

/**
 * Create a production deployment of the linked project with
 * `vercel deploy --prod --yes` (captured output with optional per-line progress,
 * long timeout — the build runs on Vercel). Never throws; failures return a
 * reason plus the output tail.
 */
export async function deployVercelProject(
  runner: VercelRunner,
  cwd: string,
  options: VercelDeployOptions = {}
): Promise<VercelDeployResult> {
  const deploy = await runVercel(runner, ['deploy', '--prod', '--yes'], {
    cwd,
    mode: 'capture',
    timeoutMs: DEPLOY_TIMEOUT_MS,
    env: options.env,
    onOutputLine: options.onLogLine
  })

  if (deploy.success) {
    return { url: parseDeployedUrl(deploy.stdout, deploy.stderr) }
  }

  return {
    failure: deploy.timedOut ? 'timeout' : 'deploy-failed',
    detail: outputTail(`${deploy.stdout}\n${deploy.stderr}`)
  }
}

/** Last non-empty lines of captured output, for inline failure detail. */
function outputTail(output: string): string | undefined {
  const lines = stripVTControlCharacters(output)
    .split('\n')
    .map((line) => line.trimEnd())
    .filter((line) => line.trim().length > 0)
  if (lines.length === 0) return undefined
  return lines.slice(-DETAIL_MAX_LINES).join('\n')
}
